/**
 * Centralised, timezone-aware date/time formatting for admin surfaces.
 *
 * Timestamps coming back from the API are UTC instants. Rendering them with a
 * bare `new Date(v).toLocaleString()` silently reinterprets them in whatever
 * timezone the browser happens to be in, with no label — so the same row reads
 * differently for staff in Accra vs. London and nobody can tell which clock a
 * value belongs to. Everything here renders in an explicit timezone and, by
 * default, shows the zone label so a timestamp is never ambiguous.
 *
 * Pass `timeZone` to pin output to a specific zone (e.g. a user's configured
 * `Africa/Accra`); omit it to use the viewer's local zone.
 */

const EM_DASH = '—';

/** The timezone the browser reports for the current viewer. */
export const viewerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const toDate = (value: unknown): Date | null => {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
};

export interface FormatOptions {
  /** IANA zone to render in (e.g. `Africa/Accra`). Defaults to the viewer's. */
  timeZone?: string;
  /** Append the zone label (e.g. `GMT+0`). Default `true`. */
  showZone?: boolean;
}

/** Date + time, timezone-aware and labelled. Falls back to the raw value. */
export const formatDateTime = (value: unknown, opts: FormatOptions = {}): string => {
  const d = toDate(value);
  if (!d) return value ? String(value) : EM_DASH;
  const { timeZone, showZone = true } = opts;
  try {
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
      ...(showZone ? { timeZoneName: 'short' } : {}),
    });
  } catch {
    // Invalid IANA zone — degrade to the viewer's local rendering.
    return d.toLocaleString();
  }
};

/** Date only (no clock), timezone-aware. Falls back to the raw value. */
export const formatDate = (value: unknown, opts: FormatOptions = {}): string => {
  const d = toDate(value);
  if (!d) return value ? String(value) : EM_DASH;
  const { timeZone } = opts;
  try {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    return d.toLocaleDateString();
  }
};

/**
 * Human summary of a timezone: the current wall-clock time there plus its GMT
 * offset — e.g. `14:32 (GMT+0)`. Returns `null` for an empty/invalid zone so
 * callers can fall back to the raw string.
 */
export const describeTimeZone = (
  timeZone?: string | null,
  now: Date = new Date(),
): string | null => {
  if (!timeZone) return null;
  try {
    const time = now.toLocaleTimeString(undefined, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
    });
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    return offset ? `${time} · ${offset}` : time;
  } catch {
    return null;
  }
};

/** Format a numeric UTC offset in hours (e.g. -7, +5.5) as `UTC-07:00`. */
export const formatUtcOffset = (hours: number): string => {
  if (!Number.isFinite(hours)) return '';
  const sign = hours < 0 ? '-' : '+';
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Current wall-clock time at a fixed UTC offset in hours, e.g. `14:32`. */
export const timeAtUtcOffset = (hours: number, now: Date = new Date()): string | null => {
  if (!Number.isFinite(hours)) return null;
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const shifted = new Date(utcMs + hours * 3_600_000);
  return shifted.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

/**
 * Render a user's stored timezone readably. The backend stores it as a numeric
 * UTC offset in hours (e.g. `-7` → UTC-07:00), but the value may also be an IANA
 * name (e.g. `Africa/Accra`). Returns `{ label, detail }`, or `null` when empty.
 */
export const describeUserTimeZone = (
  value: string | number | null | undefined,
  now: Date = new Date(),
): { label: string; detail: string | null } | null => {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (raw === '') return null;
  // Numeric offset in hours (e.g. "-7", "0", "5.5") — no letters or slashes.
  if (/^[+-]?\d+(\.\d+)?$/.test(raw)) {
    const hours = Number(raw);
    const t = timeAtUtcOffset(hours, now);
    return { label: formatUtcOffset(hours), detail: t ? `${t} local` : null };
  }
  // Otherwise treat as an IANA zone name.
  const desc = describeTimeZone(raw, now);
  return { label: raw, detail: desc ? `${desc} local` : null };
};
